import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ReactDatePicker } from 'components/reactdatepicker';
import {
  Button,
  Dialog,
  ErrorMessage,
  Flex,
  Grid,
  Input,
  RadioBox,
  Stack,
  Text,
} from 'components/ui';
import { format } from 'date-fns';
import { useBuyerOrg } from 'hooks/use-buyer-org';
import { ApiClient } from 'lib/api/client';
import {
  CALLBACK_REQUEST,
  OFFER_RECEIVED,
  getFormFieldErrorsFromResponse,
} from 'lib/helpers';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { rgba } from 'polished';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { QUERY_KEYS } from 'settings/config';
import * as yup from 'yup';

enum PriceTypeEnum {
  negotiable = 'Negotiable',
  non_negotiable = 'Non-Negotiable',
}
interface MarketplaceEnquiryDialogProps {
  openEnquiryDialog: boolean;
  closeEnquiryDialog: Function;
  type?: 'enquiry' | 'offer' | 'callback';
}
type FormData = {
  name: string;
  email: string;
  mobile: string;
  message: string;
  price?: string;
  price_type?: string;
  preferred_delivery_date?: Date | undefined;
  comments?: string;
  offer_validity?: string;
};

const enquirySchema = yup.object().shape({
  name: yup.string().required('Please enter name'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
  mobile: yup.string().nullable(),
  message: yup.string().required('Please enter message'),
});

const callBackSchema = yup.object().shape({
  name: yup.string().required('Please enter name'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
  mobile: yup.string().required('Please enter your mobile number'),
  message: yup.string(),
});

const offerSchema = yup.object().shape({
  name: yup.string().required('Please enter name'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
  mobile: yup.string().required('Please enter your mobile number'),
  price: yup.string().required('Please enter price'),
  price_type: yup
    .string()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .nullable()
    .notRequired()
    .test(
      'is-valid-price-type',
      'Please select price type',
      (value) =>
        !value || Object.values(PriceTypeEnum).includes(value as PriceTypeEnum)
    ),
  preferred_delivery_date: yup
    .date()
    .nullable()
    .notRequired()
    .typeError('Preferred delivery from date must be a valid date')
    .transform((value, originalValue) => {
      if (originalValue === '') {
        return null;
      }
      return value;
    }),
});

const offerSessionSchema = yup.object().shape({
  price: yup.string().required('Please enter price'),
  price_type: yup
    .string()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .nullable()
    .notRequired()
    .test(
      'is-valid-price-type',
      'Please select price type',
      (value) =>
        !value || Object.values(PriceTypeEnum).includes(value as PriceTypeEnum)
    ),
  preferred_delivery_date: yup
    .date()
    .nullable()
    .notRequired()
    .typeError('Preferred delivery from date must be a valid date')
    .transform((value, originalValue) => {
      if (originalValue === '') {
        return null;
      }
      return value;
    }),
});

const enquirySessionSchema = yup.object().shape({
  message: yup.string().required('Please enter message'),
});

const callbackSessionSchema = yup.object().shape({
  message: yup.string(),
});

export const MarketplaceEnquiryDialog = ({
  openEnquiryDialog,
  closeEnquiryDialog,
  type = 'enquiry',
}: MarketplaceEnquiryDialogProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const { id } = router.query;
  const { data: org } = useBuyerOrg();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(
      !session || session === null
        ? type === 'enquiry'
          ? enquirySchema
          : type === 'offer'
          ? offerSchema
          : callBackSchema
        : type === 'enquiry'
        ? enquirySessionSchema
        : type === 'offer'
        ? offerSessionSchema
        : callbackSessionSchema
    ),
  });

  const messageFormatter = (data: Record<string, any>): string => {
    const validUntil =
      data?.offer_validity && 'Valid until: ' + data?.offer_validity;
    const comments = data?.comments && 'Comments: ' + data?.comments;

    const hasValidPreferredDate =
      data?.preferred_delivery_date instanceof Date &&
      !isNaN(data?.preferred_delivery_date?.getTime?.());

    const filteredDataString = `
    Price: ${data.price}, ${data.price_type ?? ''}
    ${validUntil ?? ''}
    ${
      hasValidPreferredDate
        ? 'Preferred delivery date: ' +
          format(data.preferred_delivery_date, 'd MMM yyyy')
        : ''
    }
    ${comments ?? ''}`;
    return filteredDataString;
  };

  //send enquiry
  const sendEnquiry = useMutation(
    (data: FormData) =>
      new ApiClient().marketplacePublicListingApi.marketplaceListingEnquiryCreate(
        {
          listingId: id as string,
          listingEnquiryCreateRequest: {
            message: data.message,
            org: org?.buyer_org_id || '',
            ...((!session || session === null) && {
              name: data.name,
              mobile: data.mobile,
              email: data.email,
            }),
          },
        }
      ),
    {
      onSuccess: () => {
        closeEnquiryDialog?.();
        reset();
        toast.success(
          `${type[0].toUpperCase() + type.slice(1)} sent successfully!`
        );
      },
      onError: (error) => {
        if (axios.isAxiosError(error)) {
          // Map errors returned to relevant form fields
          if (error?.response?.data?.errors) {
            const errors = getFormFieldErrorsFromResponse<FormData>(
              error.response.data.errors
            );
            errors.forEach(({ field, message }) =>
              toast.error(`${field}: ${message}`)
            );
          }
        } else {
          toast.error(`Error sending ${type}`);
        }
      },
      onSettled: () =>
        queryClient.invalidateQueries([QUERY_KEYS.enquiryList, id]),
    }
  );

  const onSubmit = async (data: FormData) => {
    if (type === 'offer') {
      const offerData = {
        price: data.price,
        price_type: data.price_type,
        offer_validity: data.offer_validity,
        comments: data.comments,
        preferred_delivery_date: data.preferred_delivery_date,
      };

      await sendEnquiry.mutate({
        ...data,
        message: `${OFFER_RECEIVED} ${messageFormatter(offerData)}`,
      });
    } else if (type === 'callback') {
      await sendEnquiry.mutate({
        ...data,
        message: `${CALLBACK_REQUEST} ${data.message}`,
      });
    } else {
      await sendEnquiry.mutate(data);
    }
  };

  const handleDismiss = () => {
    closeEnquiryDialog?.();
    reset();
  };

  return (
    <>
      {/* Dialog box */}
      <Dialog
        title={`${
          type === 'callback'
            ? 'Request Callback'
            : type === 'offer'
            ? 'Send an offer'
            : `Send ${type[0].toUpperCase() + type.slice(1)}`
        }`}
        isOpen={openEnquiryDialog}
        overlayBackgroundColour={rgba(0, 0, 0, 0.3)}
        hasBlurredOverlay={false}
        onDismiss={handleDismiss}
        actions={
          <Button
            size='medium'
            data-cy='submitButton'
            disabled={sendEnquiry.isLoading}
            form='enquiryForm'
            type='submit'
            isLoading={sendEnquiry.isLoading}
          >
            Submit
          </Button>
        }
      >
        <form id='enquiryForm' onSubmit={handleSubmit(onSubmit)}>
          <Stack space={1}>
            {(!session || session === null) && (
              <>
                {/* name */}
                <Input
                  data-cy='name'
                  label='Name'
                  placeholder='Enter your name'
                  {...register('name')}
                  state={errors.name && 'error'}
                  message={errors.name?.message}
                />
                {/* Email */}
                <Grid columns={2} gap={2} stackOnSmall>
                  <Input
                    type='email'
                    label='Email address'
                    placeholder='calvin@email.com'
                    autoComplete='email'
                    data-cy='emailField'
                    {...register('email')}
                    state={errors.email && 'error'}
                    message={errors.email?.message}
                  />

                  {/* Phone */}
                  <Input
                    type='tel'
                    label='Mobile number'
                    placeholder='0451 553 453'
                    autoComplete='tel'
                    data-cy='phoneNumberField'
                    {...register('mobile')}
                    state={errors.mobile && 'error'}
                    message={errors.mobile?.message}
                  />
                </Grid>
              </>
            )}

            {/* message */}
            {type === 'enquiry' && (
              <Input
                as='textarea'
                label='Message'
                maxLength={250}
                data-cy='enquiry message'
                placeholder='Enter at least 10 characters'
                {...register('message')}
                state={errors.message && 'error'}
                message={errors.message?.message}
              />
            )}

            {type === 'offer' && (
              <>
                <Input
                  label='Price'
                  type='string'
                  placeholder='Enter Price'
                  state={errors.price && 'error'}
                  message={errors.price?.message}
                  {...register('price')}
                />

                <Text className='header-text' fontSize={1} fontWeight='bold'>
                  Price Type
                </Text>
                <Flex gap={1}>
                  {Object.values(PriceTypeEnum).map((type) => (
                    <RadioBox
                      key={type}
                      label={type}
                      value={type}
                      {...register('price_type')}
                    />
                  ))}
                </Flex>
                {errors.price_type && (
                  <ErrorMessage>{errors?.price_type?.message}</ErrorMessage>
                )}

                <Text
                  className='header-text-date'
                  fontWeight='bold'
                  fontSize={1}
                >
                  Preferred Delivery Date
                </Text>
                <Controller
                  control={control}
                  shouldUnregister={true}
                  name='preferred_delivery_date'
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <ReactDatePicker
                        dateFormat='d MMM yy'
                        field={field}
                        fieldStateError={error}
                      />
                    );
                  }}
                />

                <Input
                  label='Valid until (optional)'
                  placeholder='eg tomorrow, 1 hr'
                  state={errors.offer_validity && 'error'}
                  message={errors.offer_validity?.message}
                  {...register('offer_validity')}
                />

                <Text className='header-text' fontWeight='bold'>
                  Comments (optional)
                </Text>
                <Input
                  as='textarea'
                  placeholder='Comments'
                  state={errors.comments && 'error'}
                  message={errors.comments?.message}
                  {...register('comments')}
                />
              </>
            )}

            {type === 'callback' && (
              <>
                <Text>
                  Press submit and the listing owner will be sent a call back
                  request with your contact details.
                </Text>
                <Input
                  as='textarea'
                  label='Optional Message'
                  maxLength={250}
                  data-cy='enquiry message'
                  placeholder='Enter at least 10 characters'
                  {...register('message')}
                  state={errors.message && 'error'}
                  message={errors.message?.message}
                />
              </>
            )}
          </Stack>
        </form>
      </Dialog>
    </>
  );
};
